import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { LineChart as TremorLineChart } from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { ValueWithComparison } from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CarregandoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import CategoriasMaisVendidasTable from "@/components/dashboard/CategoriasMaisVendidasTable";
import CategoriasPorEstadoTable from "@/components/dashboard/CategoriasPorEstadoTable";
import CategoriasPorCidadeTable from "@/components/dashboard/CategoriasPorCidadeTable";
import ProdutosMaisVendidosTable from "@/components/dashboard/ProdutosMaisVendidosTable";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  DrilldownProvider,
  type DrillDimension,
} from "@/features/drilldown/drilldown.context";
import { ActiveFiltersDisplay } from "@/features/drilldown/ActiveFiltersDisplay";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";
import { useDrilldownOptional } from "@/features/drilldown";
import {
  getProductsGeneralItemPerformanceApiV1ProductsGeneralItemPerformanceGet,
  getProductsRevenueTrendApiV1ProductsRevenueTrendGet,
  getProductsTopCategoriesRevenueTrendApiV1ProductsTopCategoriesRevenueTrendGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  GeneralItemPerformanceResponse as GeneratedGeneralItemPerformanceResponse,
  GetProductsRevenueTrendApiV1ProductsRevenueTrendGetData,
  ProductsDashboardGeoResponse as GeneratedProductsDashboardGeoResponse,
  ProductsRevenueTrendResponse as GeneratedProductsRevenueTrendResponse,
  ProductsRevenueTrendSeriesItem as GeneratedProductsRevenueTrendSeriesItem,
  ProductsTopCategoriesRevenueTrendResponse as GeneratedProductsTopCategoriesRevenueTrendResponse,
  ProductsTopCategoriesRevenueTrendSeriesItem as GeneratedProductsTopCategoriesRevenueTrendSeriesItem,
} from "@/backend-sdk/client/types.gen";

// Todos os dados são buscados de endpoints reais - sem mocks

type GeneralItemPerformanceResponse = GeneratedGeneralItemPerformanceResponse;
type ProductsRevenueTrendResponse = GeneratedProductsRevenueTrendResponse;
type ProductsRevenueTrendSeriesItem = GeneratedProductsRevenueTrendSeriesItem;
type ProductsTopCategoriesRevenueTrendResponse =
  GeneratedProductsTopCategoriesRevenueTrendResponse;
type ProductsTopCategoriesRevenueTrendSeriesItem =
  GeneratedProductsTopCategoriesRevenueTrendSeriesItem;
type ProductsDashboardGeoResponse = GeneratedProductsDashboardGeoResponse;
type ProductsRevenueTrendQuery =
  GetProductsRevenueTrendApiV1ProductsRevenueTrendGetData["query"];
type ProductsCrossFilterKey = Extract<
  keyof ProductsRevenueTrendQuery,
  `filter_${string}`
>;
type ProductsCrossFilters = Pick<
  ProductsRevenueTrendQuery,
  ProductsCrossFilterKey
>;

const getProductsCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<ProductsCrossFilters> => ({
  filter_category: filters?.filter_category,
  // <M-212> `filter_category2` a `filter_category5`, `filter_brand` e `filter_variant` saíram:
  // `DrillDimension` não tem mais as seis dimensões, então `apiFilters` não tem como trazer
  // as chaves. As rotas continuam declarando os parâmetros — o que sumiu é quem os produzia.
  filter_product: filters?.filter_product,
  filter_region: filters?.filter_region,
  filter_city: filters?.filter_city,
});

const EMPTY_PRODUCTS_REVENUE_TREND: ProductsRevenueTrendResponse = {
  items: [],
  pagination: { page: 1, page_size: 10, total: 0, total_pages: 0 },
  series: [],
};

const EMPTY_PRODUCTS_TOP_CATEGORIES_REVENUE_TREND: ProductsTopCategoriesRevenueTrendResponse =
  {
    series: [],
  };

interface ChartMonthRow {
  month: string;
  [seriesName: string]: string | number;
}

// Função helper para formatar INTEIROS com segurança (sem casas decimais)
const formatIntegerValue = (value: unknown): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "0";
  }
  return Math.trunc(Number(value)).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  });
};

// Função helper para formatar moeda com segurança
const formatCurrency = (value: unknown): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "R$ 0,00";
  }
  return `R$ ${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
};

// Função helper para formatar percentual com segurança
const formatPercentage = (value: unknown): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "0,00%";
  }
  return `${Number(value).toFixed(2)}%`;
};

// Componente de tabela de produtos agora usa dados reais inline no JSX abaixo

/** Os sete cards saem do e-commerce do GA4. */
const FONTES_DOS_CARDS: readonly RequisitoDeFonte[] = ["ga4"];

export default function ProductsPage() {
  // Hierarquias de drill-down para esta página.
  //
  // <M-212> A lista passou a ser verdade: até aqui as duas tabelas compartilhadas ofereciam
  // **oito** dimensões de item — `category2` a `category5`, `brand` e `variant` além destas
  // duas —, e as seis que saíram não casavam com linha nenhuma, de modo que o clique esvaziava
  // a página inteira em vez de recortá-la. Esta é a única das sete telas em que o drill de item
  // é honrado pelas rotas que ela abre, e por isso a única que liga `drilldownEnabled` nas duas
  // tabelas compartilhadas — o padrão da prop é `false`.
  const hierarchies = useMemo<DrillDimension[][]>(
    () => [
      ["category", "product"],
      ["region", "city"],
    ],
    [],
  );

  return (
    <DrilldownProvider hierarchies={hierarchies}>
      <ProductsPageContent />
    </DrilldownProvider>
  );
}

function ProductsPageContent() {
  const navigate = useNavigate();
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const drilldown = useDrilldownOptional();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    null;
  const [loadingCards, setLoadingCards] = useState<boolean>(true);
  const [cardsData, setCardsData] =
    useState<GeneralItemPerformanceResponse | null>(null);
  const [loadingCategoriesTrend, setLoadingCategoriesTrend] =
    useState<boolean>(true);
  const [categoriesTrend, setCategoriesTrend] =
    useState<ProductsTopCategoriesRevenueTrendResponse | null>(null);
  const [loadingProductsTrend, setLoadingProductsTrend] =
    useState<boolean>(true);
  const [productsTrend, setProductsTrend] =
    useState<ProductsRevenueTrendResponse | null>(null);
  const [erroCards, setErroCards] = useState<boolean>(false);

  React.useEffect(() => {
    // A resposta de um filtro anterior não pode sobrescrever a do filtro atual.
    let ativo = true;

    const carregar = async () => {
      setLoadingCards(true);
      setLoadingCategoriesTrend(true);
      setLoadingProductsTrend(true);
      setErroCards(false);

      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);
      const comparison =
        filterState.comparison?.type === "previousYear"
          ? "previous_year"
          : "previous_period";
      const customerUuid =
        selectedAccount?.customer_uuid ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;
      const accountId = undefined;
      if (!customerUuid) {
        setCardsData(null);
        setLoadingCards(false);
        setLoadingCategoriesTrend(false);
        setLoadingProductsTrend(false);
        return;
      }

      const crossFilters = getProductsCrossFilters({
        ...(drilldown?.apiFilters || {}),
      });

      // As três buscas são independentes e saem juntas, cada uma liberando o seu bloco.
      // Antes iam uma depois da outra, e os cards esperavam as duas tendências e ainda o
      // `/products/dashboard` completo, cujo único uso era um estado que nada lia.
      await Promise.all([
        getProductsGeneralItemPerformanceApiV1ProductsGeneralItemPerformanceGet(
          {
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              ...crossFilters,
            },
            throwOnError: true,
          },
        )
          .then(({ data }) => {
            if (ativo) setCardsData(data);
          })
          .catch(() => {
            if (!ativo) return;
            setCardsData(null);
            setErroCards(true);
          })
          .finally(() => {
            if (ativo) setLoadingCards(false);
          }),

        getProductsRevenueTrendApiV1ProductsRevenueTrendGet({
          query: {
            start_date: startDate,
            end_date: endDate,
            comparison,
            customer_uuid: customerUuid,
            account_id: accountId,
            limit: 5,
            ...crossFilters,
          },
          throwOnError: true,
        })
          .then(({ data }) => {
            if (ativo) setProductsTrend(data || EMPTY_PRODUCTS_REVENUE_TREND);
          })
          .catch(() => {
            if (ativo) setProductsTrend(EMPTY_PRODUCTS_REVENUE_TREND);
          })
          .finally(() => {
            if (ativo) setLoadingProductsTrend(false);
          }),

        getProductsTopCategoriesRevenueTrendApiV1ProductsTopCategoriesRevenueTrendGet(
          {
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              ...crossFilters,
            },
            throwOnError: true,
          },
        )
          .then(({ data }) => {
            if (ativo)
              setCategoriesTrend(
                data || EMPTY_PRODUCTS_TOP_CATEGORIES_REVENUE_TREND,
              );
          })
          .catch(() => {
            if (ativo)
              setCategoriesTrend(EMPTY_PRODUCTS_TOP_CATEGORIES_REVENUE_TREND);
          })
          .finally(() => {
            if (ativo) setLoadingCategoriesTrend(false);
          }),
      ]);
    };
    void carregar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState,
    selectedAccount,
    drilldown?.apiFiltersKey,
    campaignFiltersNonce,
  ]);

  // loading dos gráficos
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadingCategoriesTrend(
      !categoriesTrend || (categoriesTrend.series || []).length === 0,
    );
    setLoadingProductsTrend(
      !productsTrend || (productsTrend.series || []).length === 0,
    );
  }, [categoriesTrend, productsTrend]);

  // Transformar série do backend em formato para Recharts (CATEGORIAS)
  const { chartData, categoryKeys } = useMemo(() => {
    if (
      !categoriesTrend ||
      !categoriesTrend.series ||
      categoriesTrend.series.length === 0
    ) {
      return { chartData: [], categoryKeys: [] as string[] };
    }
    // Mapear meses para objeto
    const monthMap: Record<string, ChartMonthRow> = {};
    const keys: string[] = [];
    for (const s of categoriesTrend.series as ProductsTopCategoriesRevenueTrendSeriesItem[]) {
      const key = s.category_name;
      keys.push(key);
      for (const p of s.points) {
        const month = p.month; // ISO date string
        if (!monthMap[month]) monthMap[month] = { month };
        monthMap[month][key] = p.revenue;
      }
    }
    const rows = Object.values(monthMap).sort((a, b) =>
      a.month < b.month ? -1 : 1,
    );
    return { chartData: rows, categoryKeys: keys };
  }, [categoriesTrend]);

  // Transformar série dos PRODUTOS em formato para Recharts
  const { productsChartData, productKeys } = useMemo(() => {
    if (
      !productsTrend ||
      !productsTrend.series ||
      productsTrend.series.length === 0
    ) {
      return { productsChartData: [], productKeys: [] as string[] };
    }
    // Mapear meses para objeto
    const monthMap: Record<string, ChartMonthRow> = {};
    const keysSet = new Set<string>();
    for (const s of productsTrend.series as ProductsRevenueTrendSeriesItem[]) {
      const key = s.product_name;
      if (!keysSet.has(key)) {
        keysSet.add(key);
      }
      for (const p of s.points) {
        const month = p.month; // ISO date string
        if (!monthMap[month]) monthMap[month] = { month };
        monthMap[month][key] = p.revenue;
      }
    }
    const rows = Object.values(monthMap).sort((a, b) =>
      a.month < b.month ? -1 : 1,
    );
    return { productsChartData: rows, productKeys: Array.from(keysSet) };
  }, [productsTrend]);

  const formatByType = (
    v: unknown,
    type: string | undefined,
    currencySymbol: string = "R$",
  ): string => {
    if (v === null || v === undefined) return "N/D";
    const num = Number(v);
    if (!isFinite(num) || num === 0) return "N/D";
    if (type === "CURRENCY")
      return `${currencySymbol} ${num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (type === "PERCENT") return `${num.toFixed(2)}%`;
    if (type === "NUMBER") return num.toLocaleString("pt-BR");
    return String(v);
  };

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Produtos</h1>
        <p className="text-muted-foreground">
          Dashboard completo de análise de produtos e performance
        </p>
      </div>

      {/* Filtro Global */}
      <GlobalFilter
        className="mb-6"
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={selectedAccount?.account_id}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Filtros Ativos de Cross-Filtering */}
      <ActiveFiltersDisplay />

      {/* PRIMEIRA LINHA: CARDS */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {/* Receita do Item - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Receita do Item</span>
                <HelpTooltip
                  content={getKpiTooltip("Receita do Item")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.item_revenue_data_type ?? "CURRENCY",
                    );
                    const currencySymbol = "R$";
                    const num = Number(cardsData?.item_revenue);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "CURRENCY")
                        return `${currencySymbol} ${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      if (type === "NUMBER") return n.toLocaleString("pt-BR");
                      return String(n);
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Receita do item"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = isValid
                      ? Number(cardsData?.item_revenue_change_percentage || 0)
                      : null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Ticket Médio - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Ticket Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("Ticket Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.average_ticket_data_type ?? "CURRENCY",
                    );
                    const currencySymbol = "R$";
                    const num = Number(cardsData?.average_ticket);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "CURRENCY")
                        return `${currencySymbol} ${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      if (type === "NUMBER") return n.toLocaleString("pt-BR");
                      return String(n);
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Ticket médio"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = isValid
                      ? Number(cardsData?.average_ticket_change_percentage || 0)
                      : null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
                <Button
                  variant="default"
                  size="sm"
                  className="mt-3 h-7 text-xs px-4 mx-auto block"
                  onClick={() => navigate("/dashboard/analise-ticket-medio")}
                >
                  ANALISAR
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pedidos - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Pedidos</span>
                <HelpTooltip
                  content={getKpiTooltip("Pedidos")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.transactions_data_type ?? "NUMBER",
                    );
                    const num = Number(cardsData?.transactions);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      return n.toLocaleString("pt-BR");
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Pedidos"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = isValid
                      ? Number(cardsData?.transactions_change_percentage || 0)
                      : null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Itens Comprados - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Itens Comprados</span>
                <HelpTooltip
                  content={getKpiTooltip("Itens Comprados")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.items_purchased_data_type ?? "NUMBER",
                    );
                    const num = Number(cardsData?.items_purchased);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      return n.toLocaleString("pt-BR");
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Itens comprados"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = isValid
                      ? Number(
                          cardsData?.items_purchased_change_percentage || 0,
                        )
                      : null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Quantidade Média de Itens - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Qtd. Média Itens</span>
                <HelpTooltip
                  content={getKpiTooltip("Qtd. Média Itens")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.average_item_quantity_data_type ?? "NUMBER",
                    );
                    const num = Number(cardsData?.average_item_quantity);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      return n.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Quantidade média de itens"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = isValid
                      ? Number(
                          cardsData?.average_item_quantity_change_percentage ||
                            0,
                        )
                      : null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Valor Médio Unitário - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Valor Médio Unit.</span>
                <HelpTooltip
                  content={getKpiTooltip("Valor Médio Unit.")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.average_unit_amount_data_type ?? "CURRENCY",
                    );
                    const currencySymbol = "R$";
                    const num = Number(cardsData?.average_unit_amount);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "CURRENCY")
                        return `${currencySymbol} ${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      if (type === "NUMBER") return n.toLocaleString("pt-BR");
                      return String(n);
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Valor médio unitário"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = isValid
                      ? Number(
                          cardsData?.average_unit_amount_change_percentage || 0,
                        )
                      : null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Taxa de Conversão - Melhorado visualmente */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Taxa Conversão</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Conversão")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="text-2xl font-light text-foreground">
                  {(() => {
                    const type = String(
                      cardsData?.session_conversion_rate_data_type ?? "PERCENT",
                    );
                    const num = Number(cardsData?.session_conversion_rate);
                    const isValid = Number.isFinite(num) && num !== 0;
                    const formatFn = (n: number) => {
                      if (type === "PERCENT") return `${n.toFixed(2)}%`;
                      return n.toLocaleString("pt-BR");
                    };

                    const valueNode = isValid ? (
                      <KpiNumber
                        as="span"
                        value={num}
                        format={formatFn}
                        className="font-light text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel="Taxa de conversão"
                      />
                    ) : (
                      <ValorIndisponivel fontes={FONTES_DOS_CARDS} erro={erroCards} />
                    );

                    const changePct = null;
                    return (
                      <ValueWithComparison
                        value={valueNode}
                        changePercentage={changePct}
                        align="left"
                        layout="stacked"
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEGUNDA LINHA: TABELA DE CATEGORIAS COM EXPANSÃO - Melhorada visualmente */}
      <CategoriasMaisVendidasTable excludeColumns={["cart_rate"]} drilldownEnabled />

      {/* TERCEIRA LINHA: GRÁFICO DE EVOLUÇÃO DAS CATEGORIAS - dados reais */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <CardTitle>EVOLUÇÃO EM R$ DAS 5 CATEGORIAS COM MAIS VENDAS</CardTitle>
          <CardDescription>
            Tendência de receita por categoria ao longo do tempo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingCategoriesTrend ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <ScrollableChart>
              <TremorLineChart
                className="h-96"
                data={chartData}
                index="month"
                categories={categoryKeys}
                colors={["slate", "emerald", "amber", "rose", "violet"]}
                valueFormatter={(value) => formatCurrency(value)}
                showLegend={true}
                showGridLines={true}
                showAnimation={true}
              />
            </ScrollableChart>
          )}
        </CardContent>
      </Card>

      {/* QUARTA LINHA: TABELA DE PRODUTOS MAIS VENDIDOS (server-side: busca/filtro/ordenação) */}
      <ProdutosMaisVendidosTable excludeColumns={["cart_rate"]} drilldownEnabled />

      {/* QUINTA LINHA: GRÁFICO DE EVOLUÇÃO DOS PRODUTOS - Dados Reais */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <CardTitle>EVOLUÇÃO EM R$ DOS 5 PRODUTOS MAIS VENDIDOS</CardTitle>
          <CardDescription>
            Tendência de receita dos produtos mais vendidos ao longo do tempo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingProductsTrend ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <ScrollableChart>
              <TremorLineChart
                className="h-96"
                data={productsChartData}
                index="month"
                categories={productKeys}
                colors={["slate", "emerald", "amber", "rose", "violet"]}
                valueFormatter={(value) => formatCurrency(value)}
                showLegend={true}
                showGridLines={true}
                showAnimation={true}
              />
            </ScrollableChart>
          )}
        </CardContent>
      </Card>

      {/* SEXTA LINHA: TABELAS DE LOCALIZAÇÃO - Categoria de produtos por região */}
      <div className="grid gap-4 md:grid-cols-2">
        <CategoriasPorEstadoTable />
        <CategoriasPorCidadeTable />
      </div>
    </div>
  );
}
